import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  CloudSync as SyncIcon,
  Description as DocumentIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import projectService, { ProjectContext, ProjectContextFilters } from '../../services/projectService';

interface ProjectContextListProps {
  filters: ProjectContextFilters;
  onEditContext: (context: ProjectContext) => void;
  onViewContext: (context: ProjectContext) => void;
  onRefresh: () => void;
}

const ProjectContextList: React.FC<ProjectContextListProps> = ({
  filters,
  onEditContext,
  onViewContext,
  onRefresh
}) => {
  const [contexts, setContexts] = useState<ProjectContext[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedContext, setSelectedContext] = useState<ProjectContext | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadContexts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await projectService.getProjectContexts(
        filters,
        page * rowsPerPage,
        rowsPerPage
      );
      setContexts(response.contexts);
      setTotalCount(response.total_count);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar los contextos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContexts();
  }, [filters, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, context: ProjectContext) => {
    setAnchorEl(event.currentTarget);
    setSelectedContext(context);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedContext(null);
  };

  const handleDeleteClick = () => {
    // Open confirmation dialog but keep the selectedContext so the dialog
    // can reference the target. Only close the menu anchor, do not clear
    // `selectedContext` (calling handleMenuClose would clear it).
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedContext) return;

    setDeleting(true);
    console.debug('ProjectContextList.handleDeleteConfirm: deleting context id=', selectedContext.id);
    try {
      await projectService.deleteProjectContext(selectedContext.id);
      console.debug('ProjectContextList.handleDeleteConfirm: delete succeeded id=', selectedContext.id);
      setDeleteDialogOpen(false);
      setSelectedContext(null);
      onRefresh();
      await loadContexts();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al eliminar el contexto');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedContext(null);
  };

  const getContextTypeLabel = (contextType: string) => {
    const labels: { [key: string]: string } = {
      description: 'Descripción',
      amenities: 'Amenidades',
      location_details: 'Detalles de Ubicación',
      pricing: 'Precios',
      specifications: 'Especificaciones',
      gallery: 'Galería',
      contact_info: 'Información de Contacto',
      custom: 'Personalizado'
    };
    return labels[contextType] || contextType;
  };

  const getContextTypeColor = (contextType: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' } = {
      description: 'primary',
      amenities: 'success',
      location_details: 'info',
      pricing: 'warning',
      specifications: 'secondary',
      gallery: 'success',
      contact_info: 'info',
      custom: 'secondary'
    };
    return colors[contextType] || 'secondary';
  };

  if (loading && contexts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Proyecto</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Documentos</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Creado por</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contexts.map((context) => (
                <TableRow key={context.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {context.project_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getContextTypeLabel(context.context_type)}
                      color={getContextTypeColor(context.context_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {context.title || 'Sin título'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DocumentIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {context.documents?.length || 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={context.is_active ? 'Activo' : 'Inactivo'}
                      color={context.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {context.created_by}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(context.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalles">
                      <IconButton
                        size="small"
                        onClick={() => onViewContext(context)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Más opciones">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, context)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {contexts.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={4}>
                      No se encontraron contextos de proyecto
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedContext) onViewContext(selectedContext);
          handleMenuClose();
        }}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} />
          Ver detalles
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedContext) onEditContext(selectedContext);
          handleMenuClose();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar este contexto de proyecto?
            Esta acción también eliminará todos los documentos asociados y no se puede deshacer.
          </Typography>
          {selectedContext && (
            <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
              <Typography variant="body2" color="text.secondary">
                <strong>Proyecto:</strong> {selectedContext.project_code}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Tipo:</strong> {getContextTypeLabel(selectedContext.context_type)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Título:</strong> {selectedContext.title || 'Sin título'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectContextList;